import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  adminCorrespondenceHistory,
  adminDownloadCorrespondence,
  adminResendCorrespondence,
  saveBlob,
  type ConferenceCorrespondenceItem,
} from "../api";
import { formatDateTime } from "../format";
import { PageHeader } from "../shells/PageHeader";

export function AdminCorrespondenceHistoryPage() {
  const { t, locale } = useLanguage();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState<ConferenceCorrespondenceItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await adminCorrespondenceHistory({
        q: q.trim() || undefined,
        status: status || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFilter = (e: FormEvent) => {
    e.preventDefault();
    load().catch(() => undefined);
  };

  const onDownload = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await adminDownloadCorrespondence(id);
      saveBlob(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onResend = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await adminResendCorrespondence(id);
      setMessage(t.admin.corrResendDone);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t.admin.corrHistory}
        lead={t.admin.corrHistoryLead}
        backTo="/admin/correspondence"
        actions={
          <Link to="/admin/correspondence" className="dn-btn dn-btn-ghost">
            {t.admin.correspondence}
          </Link>
        }
      />
      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}

      <form className="dn-card dn-form" onSubmit={onFilter} style={{ marginBottom: "1.25rem" }}>
        <div className="dn-grid" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: "0.75rem" }}>
          <label>
            {t.admin.corrSearchHistory}
            <input value={q} onChange={(ev) => setQ(ev.target.value)} />
          </label>
          <label>
            {t.admin.colStatus}
            <select value={status} onChange={(ev) => setStatus(ev.target.value)}>
              <option value="">{t.admin.corrStatusAll}</option>
              <option value="generated">{t.admin.corrStatusGenerated}</option>
              <option value="sent">{t.admin.corrStatusSent}</option>
              <option value="failed">{t.admin.corrStatusFailed}</option>
            </select>
          </label>
          <label>
            {t.admin.corrDateFrom}
            <input type="date" value={dateFrom} onChange={(ev) => setDateFrom(ev.target.value)} />
          </label>
          <label>
            {t.admin.corrDateTo}
            <input type="date" value={dateTo} onChange={(ev) => setDateTo(ev.target.value)} />
          </label>
          <div style={{ alignSelf: "end" }}>
            <button type="submit" className="dn-btn dn-btn-primary" disabled={busy}>
              {t.admin.corrFilter}
            </button>
          </div>
        </div>
      </form>

      <div className="dn-table-wrap">
        <table className="dn-table dn-table-responsive">
          <thead>
            <tr>
              <th>{t.admin.corrRecipient}</th>
              <th>{t.admin.corrTitle}</th>
              <th>{t.admin.corrType}</th>
              <th>{t.admin.colStatus}</th>
              <th>{t.admin.corrCreated}</th>
              <th>{t.admin.actions}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <div>{row.recipient_name}</div>
                  <div className="dn-muted" style={{ fontSize: "0.85rem" }}>
                    {row.recipient_email}
                  </div>
                </td>
                <td>{row.title}</td>
                <td>{row.recipient_type}</td>
                <td>{row.delivery_status}</td>
                <td>{formatDateTime(row.created_at, locale)}</td>
                <td>
                  <div className="dn-cta-row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
                    <button
                      type="button"
                      className="dn-btn dn-btn-ghost"
                      disabled={busy}
                      onClick={() => onDownload(row.id)}
                    >
                      {t.admin.corrDownload}
                    </button>
                    <button
                      type="button"
                      className="dn-btn dn-btn-ghost"
                      disabled={busy}
                      onClick={() => onResend(row.id)}
                    >
                      {t.admin.corrResend}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="dn-muted">
                  {t.admin.corrHistoryEmpty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
